import { ButtonBase } from '@mui/material';
import { useApp } from 'contexts/app';
import { useViewport } from 'contexts/viewport';
import { PageContext } from 'pages/InstructorViewFormativeSubmission/PageProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton, Stack, styled, Text } from 'shell/components';
import { FEEDBACK_LIMIT } from 'utils/constants';
import { getUserPreferredFullName } from 'utils/helper';

const Header = () => {
    const { studentAccountUsers } = useApp();
    const { t } = useTranslation('assignment');
    const { device } = useViewport();

    const {
        studentSubmissionsGroupedByAccountUser,
        currentSubmission,
        currentStudentSubmissions,
        currentSubmissionIndex,
        onClickPrevAttempt,
        onClickNextAttempt,
        onChangeStudent,
    } = useContext(PageContext);

    const currentStudent = studentAccountUsers.find(
        ({ owner_account_id }) => owner_account_id === currentSubmission.owner_account_id
    );
    const currentStudentIndex = studentSubmissionsGroupedByAccountUser.findIndex(
        ({ owner_account_id }) => owner_account_id === currentStudent.owner_account_id
    );

    const canClickPrev = currentSubmissionIndex > 0;
    const canClickNext = currentSubmissionIndex < currentStudentSubmissions.length - 1;

    const NameComponent = (
        <Text variant={'p1'} weight={'medium'}>
            {getUserPreferredFullName(currentStudent)}
        </Text>
    );

    const UploadTimestampComponent = (
        <Text color={'light'} variant={'p3'}>
            {t('common:ui.uploaded', 'Uploaded')}:{' '}
            {t('common:ui.format-date-short', { val: currentSubmission.created_on })} (
            {t('common:ui.format-time-only', { val: currentSubmission.created_on })})
        </Text>
    );

    const DraftSelectorComponent = (
        <Stack sx={{ flexShrink: 0 }} alignItems={'center'} direction={'row'} spacing={2}>
            <IconButton
                aria-label={'previous draft'}
                disabled={!canClickPrev}
                kind={'chevronLeft'}
                size={'small'}
                tooltip={{ component: t('controls.previous-draft', 'Previous Draft') }}
                variant={'square'}
                onClick={onClickPrevAttempt}
            />
            <Text>
                {t('draft-number', 'Draft {count}', { count: `${currentSubmissionIndex + 1}/${FEEDBACK_LIMIT}` })}
            </Text>
            <IconButton
                aria-label={'next draft'}
                disabled={!canClickNext}
                kind={'chevronRight'}
                size={'small'}
                tooltip={{ component: t('controls.next-draft', 'Next Draft') }}
                variant={'square'}
                onClick={onClickNextAttempt}
            />
        </Stack>
    );

    const StudentSelectorComponent = (
        <Dropdown
            id={'select-student-dropdown'}
            options={studentSubmissionsGroupedByAccountUser.map((accountUser) => {
                return {
                    key: accountUser.owner_account_id,
                    value: getUserPreferredFullName(accountUser),
                    disabled: !Boolean(accountUser.submissions.length),
                };
            })}
            rootComponent={
                <ButtonBase>
                    <Stack direction={'row'} spacing={2}>
                        <Text weight={'semibold'}>Student</Text>
                        <Text>
                            {currentStudentIndex + 1}/{studentSubmissionsGroupedByAccountUser.length}
                        </Text>
                        <Icon kind={'dropdownArrowDown'} />
                    </Stack>
                </ButtonBase>
            }
            onSelect={onChangeStudent}
        />
    );

    return device === 'mobile' ? (
        <CustomStack device={device} spacing={2}>
            <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                <Stack spacing={1}>
                    {NameComponent}
                    {UploadTimestampComponent}
                </Stack>
                {DraftSelectorComponent}
            </Stack>
            {StudentSelectorComponent}
        </CustomStack>
    ) : (
        <CustomStack device={device} alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
            <Stack spacing={1}>
                {NameComponent}
                {UploadTimestampComponent}
            </Stack>
            <Stack alignItems={'center'} direction={'row'} spacing={4}>
                {DraftSelectorComponent}
                {StudentSelectorComponent}
            </Stack>
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ theme, device }) => {
    return {
        position: 'absolute',
        top: 0,
        borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        width: '100%',
        height: device === 'mobile' ? '113px' : '65px',
        padding: theme.spacing(3, 5),
        background: theme.palette.ensightful.primary[50],
    };
});

export default Header;
